import { useMemo, useRef, useState } from "react";

import { Check, Lock, Paperclip, Send } from "lucide-react";
import { useDispatch } from "react-redux";

import type {
  ProviderDocumentRequestSchema,
  ProviderRequestTypeEnum,
  RequestFormGroupSchema,
} from "@/api/generated/types.gen";

import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Skeleton } from "@components/ui/skeleton";
import { Textarea } from "@components/ui/textarea";

import { CONSENT_ACCEPT, REQUEST_TYPE_TABS } from "@constants/provider-requests";

import {
  type UploadedConsent,
  useConsentUpload,
  useCreateDocumentRequest,
} from "@hooks/mutations/use-provider-requests";
import { useRequestForms } from "@hooks/queries/use-provider-requests";

import { notifyError, showSuccess } from "@utils/notification";

/** A selectable service-component chip. */
const ItemChip = ({
  label,
  selected,
  onToggle,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={selected}
    onClick={onToggle}
    className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
      selected
        ? "border-primary bg-primary/5 text-foreground"
        : "border-border bg-background-white text-foreground hover:border-primary/40"
    }`}
  >
    <Check
      className={`h-4 w-4 ${selected ? "text-primary" : "text-foreground-muted/50"}`}
    />
    {label}
  </button>
);

type Props = {
  caseId: string;
  caseNumber: string;
  /** Called with the created request once it's raised. */
  onSent?: (request: ProviderDocumentRequestSchema) => void;
};

/**
 * The "request documents from a provider" form (provider, request type,
 * service-component picker, comment, ClientAuth consent, provider ref). Reused
 * on the case page and in the referrer new-case wizard's request step.
 */
export const RequestDocumentsForm = ({ caseId, caseNumber, onSent }: Props) => {
  const dispatch = useDispatch();

  const { data: catalogue, isLoading: isCatalogueLoading } = useRequestForms();

  const [requestType, setRequestType] = useState<ProviderRequestTypeEnum>("records");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState("");
  const [providerReference, setProviderReference] = useState("");
  const [consent, setConsent] = useState<UploadedConsent | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadConsent, isUploading } = useConsentUpload();
  const createRequest = useCreateDocumentRequest(caseId);

  const groups: RequestFormGroupSchema[] = useMemo(
    () => catalogue?.request_forms?.[requestType] ?? [],
    [catalogue, requestType]
  );

  const handleTypeChange = (next: ProviderRequestTypeEnum) => {
    if (next === requestType) return;
    // Items are per request type — reset the selection when switching.
    setRequestType(next);
    setSelected(new Set());
  };

  const toggleItem = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleConsentSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file || !caseNumber) return;
    try {
      setConsent(await uploadConsent({ file, caseNumber }));
    } catch (err) {
      // No customMessage, for the same reason as the submit handler below --
      // and it matters more here. This is a direct-to-blob upload, which is
      // exactly what the CSP branch in getFriendlyErrorMessage was written
      // for; a policy block previously cost a day of being chased as a network
      // fault, and a customMessage hides that mapping completely. It also
      // hides the SAS endpoint's "you don't have permission to upload to this
      // case" 403 behind an opaque "Failed to attach consent".
      notifyError(err, dispatch);
    }
  };

  const canSubmit =
    !!caseId && !!consent && selected.size > 0 && !createRequest.isPending;

  const handleSubmit = async () => {
    if (!caseId || !consent || selected.size === 0) return;
    try {
      const created = await createRequest.mutateAsync({
        request_type: requestType,
        requested_items: Array.from(selected),
        comment: comment.trim() || null,
        provider_reference: providerReference.trim() || null,
        consent_file_path: consent.blobPath,
        consent_filename: consent.filename,
      });
      showSuccess("Document request sent to the provider.", dispatch);
      if (created) onSent?.(created);
    } catch (err) {
      // No customMessage (MDRS-624): notifyError is
      // `customMessage || getFriendlyErrorMessage(err)`, so passing one here
      // meant every failure on this path -- consent scoped to another case,
      // only-once conflict, provider dispatch failure -- collapsed into the
      // same flat "Failed to send the request", and the error-copy mapping was
      // never consulted at all. Each of those three now has its own branch in
      // getFriendlyErrorMessage; dropping the argument is what lets them run.
      notifyError(err, dispatch);
    }
  };

  return (
    <div className="space-y-6">
      {/* Select provider */}
      <section>
        <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-foreground-muted">
          Select provider
        </h2>
        <span className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary/5 px-4 py-2 text-sm font-semibold text-foreground">
          <Check className="h-4 w-4 text-primary" />
          SureMed
        </span>
      </section>

      {/* Request type */}
      <section>
        <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-foreground-muted">
          Request type
        </h2>
        <div className="inline-flex rounded-full bg-background p-1">
          {REQUEST_TYPE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTypeChange(tab.id)}
              aria-pressed={requestType === tab.id}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                requestType === tab.id
                  ? "bg-primary text-white"
                  : "text-foreground hover:text-primary"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* What to request */}
      <section>
        <h2 className="text-xs font-bold uppercase tracking-wide text-foreground-muted">
          What to request
        </h2>
        <p className="mb-4 mt-1 text-sm text-foreground-muted">
          Choose the record types needed — the provider decides which documents
          fulfil each.
        </p>

        {isCatalogueLoading ? (
          <Skeleton className="h-10 w-2/3" />
        ) : (
          <div className="space-y-5">
            {groups.map((group) => (
              <div key={group.label}>
                <h3 className="mb-2 text-sm font-bold text-foreground">
                  {group.label}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {group.items.map((item) => (
                    <ItemChip
                      key={item.code}
                      label={item.label}
                      selected={selected.has(item.code)}
                      onToggle={() => toggleItem(item.code)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Comment */}
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        placeholder="Comment for the provider — specify what's needed (e.g. records from date of injury, imaging reports, specialist correspondence)"
      />

      {/* Signed consent */}
      <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
        <div className="flex items-start gap-3">
          <Lock className="mt-0.5 h-5 w-5 text-foreground-muted" />
          <div>
            <p className="font-bold text-foreground">Signed consent (ClientAuth)</p>
            <p className="text-sm text-foreground-muted">
              {consent
                ? `Attached: ${consent.filename}`
                : "Required before this request can send."}
            </p>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={CONSENT_ACCEPT}
          className="hidden"
          aria-label="Attach signed consent"
          onChange={handleConsentSelected}
        />
        <Button
          variant="simple-outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          <Paperclip className="mr-2 h-4 w-4" />
          {isUploading
            ? "Attaching…"
            : consent
              ? "Replace consent"
              : "Attach consent"}
        </Button>
      </div>

      {/* Provider reference + submit */}
      <div className="flex items-center gap-4">
        <Input
          value={providerReference}
          onChange={(e) => setProviderReference(e.target.value)}
          placeholder="Provider reference or member no. (optional)"
          className="flex-1"
        />
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          title={
            !consent
              ? "Attach signed consent before sending"
              : selected.size === 0
                ? "Select at least one record type"
                : undefined
          }
        >
          <Send className="mr-2 h-4 w-4" />
          {createRequest.isPending ? "Sending…" : "Request documents"}
        </Button>
      </div>

      <p className="text-sm text-foreground-muted">
        The provider is billed separately. Collected documents are added to this case
        once received.
      </p>
    </div>
  );
};
